/**
 * Tasks API Client
 *
 * API functions for task CRUD operations.
 */

import { apiRequest } from './api';

/**
 * Task list item
 */
export interface TaskListItem {
  id: number;
  name: string;
  beschrijving: string | null;
  weging: number;
  isActief: boolean;
  competencies: Array<{ id: number; name: string }>;
  employeesCount: number;
  createdAt: string;
}

/**
 * Task detail
 */
export interface TaskDetail extends TaskListItem {
  updatedAt: string;
}

/**
 * Task list response
 */
export interface TaskListResponse {
  data: TaskListItem[];
  total: number;
}

/**
 * Task list filters
 */
export interface TaskFilters {
  actief?: boolean;
}

/**
 * Create task input
 */
export interface CreateTaskInput {
  name: string;
  beschrijving?: string;
  weging: number;
  isActief?: boolean;
}

/**
 * Update task input
 */
export interface UpdateTaskInput {
  name?: string;
  beschrijving?: string;
  weging?: number;
  isActief?: boolean;
}

/**
 * Tasks API functions
 */
export const tasksApi = {
  /**
   * List all tasks with optional filters
   */
  async list(filters?: TaskFilters): Promise<TaskListResponse> {
    const params = new URLSearchParams();
    if (filters?.actief !== undefined) params.append('actief', String(filters.actief));

    const query = params.toString();
    return apiRequest<TaskListResponse>(`/tasks${query ? `?${query}` : ''}`);
  },

  /**
   * Get task by ID
   */
  async get(id: number): Promise<TaskDetail> {
    return apiRequest<TaskDetail>(`/tasks/${id}`);
  },

  /**
   * Create new task
   */
  async create(input: CreateTaskInput): Promise<TaskDetail> {
    return apiRequest<TaskDetail>('/tasks', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update task
   */
  async update(id: number, input: UpdateTaskInput): Promise<TaskDetail> {
    return apiRequest<TaskDetail>(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Delete task
   */
  async delete(id: number): Promise<void> {
    return apiRequest<void>(`/tasks/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Toggle task active status
   */
  async toggleActive(id: number): Promise<TaskDetail> {
    return apiRequest<TaskDetail>(`/tasks/${id}/toggle-active`, {
      method: 'PATCH',
    });
  },
};
